import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { StarRating } from '../components/ProductCard';
import api from '../utils/api';
import { toast } from 'sonner';
import { Heart, ShoppingBag, Star, User, Truck, RefreshCw, CreditCard, Shield, Palette, Scissors, Sparkles, Tag, Share2, Copy, ExternalLink, AlertTriangle, Package, ZoomIn, Camera, Upload, X, Trash2, Loader2, Info } from 'lucide-react';

// ============= Vastram Mirror Modal =============
const CATEGORY_GUIDANCE = {
  'saree': "For best results, please upload a photo where you are already wearing a saree or a similar full-length draped outfit. Make sure your full body is visible and the photo is well-lit.",
  'suit': "For best results, please upload a photo where you are already wearing a suit & salwar or a similar two-piece outfit. Make sure your full body is visible and the photo is well-lit.",
  'kurti': "For best results, please upload a photo where you are already wearing a kurti or a similar top. Make sure your upper body is clearly visible and the photo is well-lit.",
  'lehenga': "For best results, please upload a photo where you are already wearing a lehenga or a similar full-length outfit. Make sure your full body is visible and the photo is well-lit.",
};
const DEFAULT_GUIDANCE = "For best results, upload a clear, well-lit full-body photo wearing clothing similar to this product.";

const MirrorPhotoModal = ({ onClose, onPhotoReady, productCategory, hasExistingPhoto }) => {
  const [mode, setMode] = useState(null); // null, 'upload', 'camera'
  const [uploading, setUploading] = useState(false);
  const [showWhyNeeded, setShowWhyNeeded] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  const guidanceText = CATEGORY_GUIDANCE[productCategory?.toLowerCase()] || DEFAULT_GUIDANCE;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => { return () => stopCamera(); }, [stopCamera]);

  const startCamera = async () => {
    setMode('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 960 } } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; }
      setCameraReady(true);
    } catch (e) {
      toast.error('Could not access camera. Please allow camera permissions.');
      setMode(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'mirror_photo.jpg');
      const res = await api.post('/vastram-mirror/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo saved to your profile');
      onPhotoReady(res.data.photo_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Only JPG, PNG, WEBP allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Must be under 10MB'); return; }
    await uploadBlob(file);
  };

  const handleUseCaptured = async () => {
    if (!capturedImage) return;
    const res = await fetch(capturedImage);
    const blob = await res.blob();
    await uploadBlob(blob);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="mirror-photo-modal">
      <div className="bg-ivory rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gold/20">
          <div>
            <h3 className="font-heading text-xl text-forest">Your Photo</h3>
            <p className="text-xs text-muted-foreground mt-1">Upload or capture a photo for virtual try-on</p>
          </div>
          <button onClick={() => { stopCamera(); onClose(); }} className="p-2 hover:bg-neutral-warm rounded-sm transition-colors">
            <X className="w-5 h-5 text-forest" />
          </button>
        </div>

        <div className="p-6">
          {/* Guidance info box */}
          <div className="mb-5 bg-blue-50 border border-blue-200 rounded-sm p-4" data-testid="mirror-photo-guidance">
            <div className="flex items-start space-x-2.5">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="text-sm text-blue-800">{guidanceText}</p>
                <button type="button" onClick={() => setShowWhyNeeded(!showWhyNeeded)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline transition-colors"
                  data-testid="mirror-why-needed-toggle">
                  {showWhyNeeded ? 'Hide details' : 'Why is this needed?'}
                </button>
                {showWhyNeeded && (
                  <p className="text-xs text-blue-700 bg-blue-100/60 rounded p-2.5 leading-relaxed">
                    Vastram Mirror works best when your uploaded photo's outfit matches the type of garment you want to try on. This helps the AI accurately replace the garment and produce a realistic result.
                  </p>
                )}
              </div>
            </div>
          </div>

          {hasExistingPhoto && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 flex items-center justify-between">
              <p className="text-xs text-amber-800">You already have a saved photo. Upload a new one if it doesn't match this product type.</p>
              <button type="button" onClick={() => { setMode(null); setCapturedImage(null); }}
                className="text-xs text-amber-700 font-medium hover:text-amber-900 underline ml-3 whitespace-nowrap">
                Replace Photo
              </button>
            </div>
          )}

          {!mode && !capturedImage && (
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gold/40 rounded-sm cursor-pointer hover:border-forest hover:bg-forest/5 transition-all group">
                <Upload className="w-8 h-8 text-gold group-hover:text-forest mb-3 transition-colors" />
                <span className="text-sm text-forest font-medium">Upload Photo</span>
                <span className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" data-testid="mirror-file-input" />
              </label>
              <button onClick={startCamera}
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gold/40 rounded-sm cursor-pointer hover:border-forest hover:bg-forest/5 transition-all group"
                data-testid="mirror-camera-button">
                <Camera className="w-8 h-8 text-gold group-hover:text-forest mb-3 transition-colors" />
                <span className="text-sm text-forest font-medium">Capture Photo</span>
                <span className="text-xs text-muted-foreground mt-1">Use device camera</span>
              </button>
            </div>
          )}

          {mode === 'camera' && !capturedImage && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-sm overflow-hidden aspect-[3/4]">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
                {!cameraReady && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex space-x-3">
                <button onClick={() => { stopCamera(); setMode(null); }} className="flex-1 py-3 text-sm border border-forest/30 text-forest hover:bg-forest/5 transition-colors">
                  Cancel
                </button>
                <button onClick={capturePhoto} disabled={!cameraReady}
                  className="flex-1 py-3 text-sm bg-forest text-ivory hover:bg-gold hover:text-forest transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  data-testid="mirror-capture-button">
                  <Camera className="w-4 h-4" /><span>Capture</span>
                </button>
              </div>
            </div>
          )}

          {capturedImage && (
            <div className="space-y-4">
              <img src={capturedImage} alt="Captured" className="w-full aspect-[3/4] object-cover rounded-sm" style={{ transform: 'scaleX(-1)' }} />
              <div className="flex space-x-3">
                <button onClick={() => { setCapturedImage(null); setMode(null); }} className="flex-1 py-3 text-sm border border-forest/30 text-forest hover:bg-forest/5 transition-colors">
                  Retake
                </button>
                <button onClick={handleUseCaptured} disabled={uploading}
                  className="flex-1 py-3 text-sm bg-forest text-ivory hover:bg-gold hover:text-forest transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  data-testid="mirror-use-photo-button">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span>{uploading ? 'Saving...' : 'Use This Photo'}</span>
                </button>
              </div>
            </div>
          )}

          {uploading && mode !== 'camera' && !capturedImage && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-forest animate-spin" />
              <span className="ml-3 text-sm text-forest">Saving photo...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============= Main Component =============
const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [reviews, setReviews] = useState([]);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [showShare, setShowShare] = useState(false);
  const imgRef = useRef(null);
  const shareRef = useRef(null);

  // Vastram Mirror state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [mirrorPhoto, setMirrorPhoto] = useState(null);
  const [mirrorUsage, setMirrorUsage] = useState({ remaining: 3, used_today: 0 });
  const [mirrorResult, setMirrorResult] = useState(null);
  const [mirrorGenerating, setMirrorGenerating] = useState(false);
  const [enlargedReviewImage, setEnlargedReviewImage] = useState(null);

  // Close share on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, revRes] = await Promise.all([
          api.get(`/products/${productId}`),
          api.get(`/reviews/product/${productId}`)
        ]);
        setProduct(prodRes.data);
        setReviews(revRes.data || []);
        if (prodRes.data?.sizes?.length > 0) setSelectedSize(prodRes.data.sizes[0]);
      } catch { navigate('/products'); }
    };
    fetchData();
    setSelectedImage(0);
    setMirrorResult(null);
  }, [productId, navigate]);

  // Fetch mirror data when logged in
  useEffect(() => {
    if (!user) return;
    const fetchMirrorData = async () => {
      try {
        const [photoRes, usageRes, resultRes] = await Promise.all([
          api.get('/vastram-mirror/photo'),
          api.get('/vastram-mirror/usage'),
          api.get(`/vastram-mirror/results/${productId}`)
        ]);
        setMirrorPhoto(photoRes.data?.photo_url || null);
        setMirrorUsage(usageRes.data || { remaining: 3, used_today: 0 });
        setMirrorResult(resultRes.data?.result || null);
      } catch {}
    };
    fetchMirrorData();
  }, [user, productId]);

  if (!product) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-forest animate-spin" /></div>;

  const isOutOfStock = product.stock !== undefined && product.stock <= 0;
  const isLowStock = !isOutOfStock && product.stock !== undefined && product.stock <= 5;
  const isSuit = product.category?.toLowerCase() === 'suit';
  const inWishlist = user ? isInWishlist(product.product_id) : false;

  const handleAddToCart = async () => {
    if (isOutOfStock) return;
    if (product.sizes?.length > 0 && !selectedSize) { toast.error('Please select a size'); return; }
    await addToCart(product.product_id, 1, selectedSize);
    toast.success('Added to bag');
  };

  const handleWishlistClick = () => {
    if (!user) { navigate('/login'); return; }
    toggleWishlist(product.product_id);
  };

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setZoomPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
  };

  const pageUrl = window.location.href;
  const shareText = `Check out ${product.name} on Vastram - ₹${product.price?.toLocaleString()}`;
  const handleCopyLink = () => { navigator.clipboard.writeText(pageUrl); toast.success('Link copied!'); setShowShare(false); };

  // Mirror handlers
  const handleMirrorClick = () => {
    if (!user) { navigate('/login'); return; }
    if (mirrorUsage.remaining <= 0) { toast.error("You've used all 3 Vastram Mirror tries for today. Come back tomorrow!"); return; }
    if (mirrorPhoto) {
      generateTryOn();
    } else {
      setShowPhotoModal(true);
    }
  };

  const handlePhotoReady = (url) => {
    setMirrorPhoto(url);
    setShowPhotoModal(false);
    generateTryOn();
  };

  const generateTryOn = async () => {
    setMirrorGenerating(true);
    try {
      // Step 1: Submit the job (returns immediately)
      const submitRes = await api.post('/vastram-mirror/tryon', { product_id: productId });
      const jobId = submitRes.data.job_id;
      setMirrorUsage(prev => ({ ...prev, remaining: submitRes.data.remaining, used_today: prev.used_today + 1 }));

      // Step 2: Poll for completion
      const pollForResult = async () => {
        const maxAttempts = 60; // ~3 minutes at 3s intervals
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const statusRes = await api.get(`/vastram-mirror/tryon/status/${jobId}`);
            const { status, result_image_url, error } = statusRes.data;
            if (status === 'completed' && result_image_url) {
              setMirrorResult({ product_id: productId, result_image_url, created_at: new Date().toISOString() });
              toast.success('Virtual try-on generated!');
              return;
            }
            if (status === 'failed') {
              toast.error(error || 'Try-on generation failed. Please try again.');
              return;
            }
            // Still processing — continue polling
          } catch {
            // Network hiccup — keep polling
          }
        }
        toast.error('Try-on is taking too long. Please check back later.');
      };
      await pollForResult();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Try-on failed. Please try again.';
      toast.error(msg);
      if (msg.includes('No photo saved')) setShowPhotoModal(true);
    }
    setMirrorGenerating(false);
  };

  const handleDeleteResult = async () => {
    try {
      await api.delete(`/vastram-mirror/results/${productId}`);
      setMirrorResult(null);
      toast.success('Try-on result removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Remove your saved photo? You can upload a new one anytime.')) return;
    try {
      await api.delete('/vastram-mirror/photo');
      setMirrorPhoto(null);
      toast.success('Photo removed');
    } catch {}
  };

  const ratingColor = (r) => r >= 3.5 ? 'text-green-500' : r >= 2 ? 'text-orange-500' : 'text-red-500';

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="product-detail-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Left: Images & Video */}
        <div className="md:sticky md:top-20">
          {/* Main display - 4:5 aspect ratio, constrained to fit with thumbnails */}
          {selectedImage === 'video' ? (
            <div className="relative overflow-hidden aspect-[4/5] max-h-[calc(100vh-14rem)] bg-black flex items-center justify-center" data-testid="product-video-container">
              {product.video_type === 'youtube' && product.video_youtube_id ? (
                <iframe src={`https://www.youtube.com/embed/${product.video_youtube_id}?rel=0&playsinline=1`}
                  title="Product Video" className="w-full h-full" frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : product.video_type === 'gdrive' ? (
                <iframe src={product.video_url}
                  title="Product Video" className="w-full h-full" frameBorder="0"
                  allow="autoplay; encrypted-media" allowFullScreen />
              ) : product.video_url ? (
                <video controls playsInline webkit-playsinline="true" className="w-full h-full object-contain" src={product.video_url}>Your browser does not support video.</video>
              ) : null}
            </div>
          ) : (
            <div className="relative overflow-hidden aspect-[4/5] max-h-[calc(100vh-14rem)] cursor-crosshair group"
              ref={imgRef} onMouseEnter={() => setShowZoom(true)} onMouseLeave={() => setShowZoom(false)} onMouseMove={handleMouseMove}
              data-testid="product-image-container">
              <img src={product.images?.[selectedImage] || '/placeholder.jpg'} alt={product.name}
                className="w-full h-full object-cover" data-testid="product-main-image" />
              <div className="absolute top-3 right-3 bg-white/80 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4 text-forest" />
              </div>
              {isLowStock && (
                <div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-3 py-1.5 flex items-center space-x-1" data-testid="low-stock-badge">
                  <AlertTriangle className="w-3 h-3" /><span>Only {product.stock} left!</span>
                </div>
              )}
              {isOutOfStock && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="bg-white text-forest text-sm uppercase tracking-wider px-6 py-3 font-medium" data-testid="out-of-stock-badge">Out of Stock</span>
                </div>
              )}
            </div>
          )}

          {showZoom && selectedImage !== 'video' && product.images?.[selectedImage] && (
            <div className="hidden md:block fixed right-8 top-32 w-[400px] h-[500px] border border-gold/30 shadow-2xl bg-white z-40 overflow-hidden" data-testid="zoom-panel">
              <div className="w-full h-full" style={{ backgroundImage: `url(${product.images[selectedImage]})`, backgroundSize: '250%', backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`, backgroundRepeat: 'no-repeat' }} />
            </div>
          )}

          {/* Thumbnails — always visible below main image */}
          {(product.images?.length > 0 || product.video_youtube_id || product.video_url || product.video_gdrive_id) && (
            <div className="flex space-x-2 mt-3 overflow-x-auto pb-1 flex-shrink-0">
              {product.images?.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)}
                  className={`w-16 h-20 overflow-hidden border-2 flex-shrink-0 transition-all ${selectedImage === i ? 'border-gold scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  data-testid={`thumbnail-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {(product.video_youtube_id || product.video_url || product.video_gdrive_id) && (
                <button onClick={() => setSelectedImage('video')}
                  className={`w-16 h-20 overflow-hidden border-2 flex-shrink-0 transition-all relative ${selectedImage === 'video' ? 'border-gold scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  data-testid="thumbnail-video">
                  <img src={product.video_youtube_id ? `https://img.youtube.com/vi/${product.video_youtube_id}/mqdefault.jpg` : product.images?.[0]} alt="Video" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Product Info Cards */}
          <div className="grid grid-cols-2 gap-3 mt-4" data-testid="product-detail-cards">
            <div className="bg-neutral-warm p-4 rounded-sm flex items-start space-x-3">
              <Scissors className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div><p className="text-xs uppercase tracking-wider text-gold mb-0.5">Fabric</p><p className="text-sm text-forest font-medium capitalize">{product.fabric}</p></div>
            </div>
            <div className="bg-neutral-warm p-4 rounded-sm flex items-start space-x-3">
              <Palette className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div><p className="text-xs uppercase tracking-wider text-gold mb-0.5">Color</p><p className="text-sm text-forest font-medium capitalize">{product.color}</p></div>
            </div>
            {product.occasion && (
              <div className="bg-neutral-warm p-4 rounded-sm flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div><p className="text-xs uppercase tracking-wider text-gold mb-0.5">Occasion</p><p className="text-sm text-forest font-medium capitalize">{product.occasion}</p></div>
              </div>
            )}
            <div className="bg-neutral-warm p-4 rounded-sm flex items-start space-x-3">
              <Tag className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div><p className="text-xs uppercase tracking-wider text-gold mb-0.5">Category</p><p className="text-sm text-forest font-medium capitalize">{product.category}</p></div>
            </div>
          </div>

          {product.story && (
            <div className="mt-6 bg-neutral-warm p-5 rounded-sm border-l-4 border-gold" data-testid="product-story">
              <p className="text-xs uppercase tracking-wider text-gold mb-2">The Story</p>
              <p className="text-sm text-forest/80 leading-relaxed italic">{product.story}</p>
            </div>
          )}
        </div>

        {/* Right: Info */}
        <div className="space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{product.category}</p>
            <h1 className="font-heading text-3xl text-forest mb-2" data-testid="product-name">{product.name}</h1>
            {product.avg_rating > 0 && (
              <div className="flex items-center space-x-2 mb-3" data-testid="product-avg-rating">
                <StarRating rating={product.avg_rating} size="w-4 h-4" />
                <span className={`text-sm font-medium ${ratingColor(product.avg_rating)}`}>{product.avg_rating}</span>
                <span className="text-sm text-muted-foreground">({product.rating_count} {product.rating_count === 1 ? 'rating' : 'ratings'})</span>
              </div>
            )}
            <div data-testid="product-price-section">
              <div className="flex items-baseline gap-3">
                <p className="font-heading text-2xl text-forest" data-testid="product-price">{'\u20B9'}{product.price?.toLocaleString()}</p>
                {product.original_price && product.original_price > product.price && (
                  <>
                    <p className="text-lg text-muted-foreground line-through" data-testid="product-original-price">{'\u20B9'}{product.original_price?.toLocaleString()}</p>
                    <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-0.5" data-testid="product-discount-badge">
                      {Math.round((1 - product.price / product.original_price) * 100)}% OFF
                    </span>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</p>
            </div>
            {isLowStock && (
              <p className="text-sm text-red-600 mt-1 flex items-center space-x-1" data-testid="low-stock-text">
                <AlertTriangle className="w-4 h-4" /><span>Hurry! Only {product.stock} left in stock</span>
              </p>
            )}
          </div>
          <p className="text-muted-foreground leading-relaxed">{product.description}</p>

          {isSuit && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm flex items-start space-x-3" data-testid="unstitched-notice">
              <Package className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div><p className="text-sm text-amber-800 font-medium">Unstitched Material</p><p className="text-xs text-amber-700">This suit comes as unstitched fabric. Stitching is not included.</p></div>
            </div>
          )}

          {/* Color Variants */}
          {product.color_variants?.length > 0 && (
            <div data-testid="color-variants">
              <p className="text-sm uppercase tracking-[0.15em] text-forest mb-3">Available Colors</p>
              <div className="flex flex-wrap gap-3">
                <div className="relative group">
                  <button className="w-9 h-9 rounded-full border-2 border-forest shadow-sm ring-2 ring-forest ring-offset-2"
                    style={{ backgroundColor: product.color?.toLowerCase().replace(/\s+/g, '') }}
                    data-testid="current-color-swatch" />
                  <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">{product.color}</span>
                </div>
                {product.color_variants.map(v => (
                  <div key={v.product_id} className="relative group">
                    <button onClick={() => navigate(`/product/${v.product_id}`)}
                      className="w-9 h-9 rounded-full border-2 border-gold/40 hover:border-forest shadow-sm hover:ring-2 hover:ring-forest hover:ring-offset-2 transition-all"
                      style={{ backgroundColor: v.color_name?.toLowerCase().replace(/\s+/g, '') }}
                      data-testid={`variant-${v.product_id}`} />
                    <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">{v.color_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-forest mb-3">Select Size</p>
              <div className="flex space-x-3">
                {product.sizes.map(size => (
                  <button key={size} onClick={() => setSelectedSize(size)}
                    className={`px-4 py-2 border text-sm transition-colors ${selectedSize === size ? 'border-forest bg-forest text-ivory' : 'border-gold/50 hover:border-forest'}`}
                    data-testid={`size-option-${size}`}>{size}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button onClick={handleAddToCart} disabled={isOutOfStock}
              className={`flex-1 py-4 text-sm uppercase tracking-[0.15em] flex items-center justify-center space-x-2 transition-colors ${isOutOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-forest text-ivory hover:bg-forest/90'}`}
              data-testid="add-to-cart-button">
              <ShoppingBag className="w-4 h-4" /><span>{isOutOfStock ? 'Out of Stock' : 'Add to Bag'}</span>
            </button>
            <button onClick={handleWishlistClick}
              className={`px-5 py-4 border transition-colors ${inWishlist ? 'border-red-300 bg-red-50' : 'border-gold/50 hover:border-forest'}`}
              data-testid="wishlist-toggle-button">
              <Heart className={`w-5 h-5 ${inWishlist ? 'fill-red-500 text-red-500' : 'text-forest'}`} />
            </button>
            {/* Vastram Mirror Button */}
            <button onClick={handleMirrorClick} disabled={mirrorGenerating}
              className="relative px-5 py-4 border border-gold/50 hover:border-gold hover:bg-gold/10 transition-all group disabled:opacity-60"
              data-testid="mirror-tryon-button"
              title={mirrorUsage.remaining > 0 ? `Try on (${mirrorUsage.remaining} left today)` : 'No tries left today'}>
              <Sparkles className={`w-5 h-5 text-gold transition-transform ${mirrorGenerating ? 'animate-spin' : 'group-hover:scale-110'}`} />
              {/* Badge */}
              <span className={`absolute -top-2 -right-2 text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold ${mirrorUsage.remaining > 0 ? 'bg-gold text-forest' : 'bg-gray-400 text-white'}`}
                data-testid="mirror-usage-badge">
                {mirrorUsage.remaining}
              </span>
            </button>
            {/* Share */}
            <div className="relative" ref={shareRef}>
              <button onClick={() => setShowShare(!showShare)} className="px-4 py-4 border border-gold/50 hover:border-forest transition-colors" data-testid="share-button">
                <Share2 className="w-5 h-5 text-forest" />
              </button>
              {showShare && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gold/30 shadow-xl rounded-sm w-48 z-30 py-2" data-testid="share-dropdown">
                  <button onClick={handleCopyLink} className="flex items-center space-x-2 w-full px-4 py-2.5 text-sm text-forest hover:bg-neutral-warm transition-colors" data-testid="share-copy-link"><Copy className="w-4 h-4" /><span>Copy Link</span></button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + pageUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 w-full px-4 py-2.5 text-sm text-forest hover:bg-neutral-warm transition-colors" data-testid="share-whatsapp"><ExternalLink className="w-4 h-4" /><span>WhatsApp</span></a>
                  <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 w-full px-4 py-2.5 text-sm text-forest hover:bg-neutral-warm transition-colors" data-testid="share-twitter"><ExternalLink className="w-4 h-4" /><span>Twitter</span></a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 w-full px-4 py-2.5 text-sm text-forest hover:bg-neutral-warm transition-colors" data-testid="share-facebook"><ExternalLink className="w-4 h-4" /><span>Facebook</span></a>
                </div>
              )}
            </div>
          </div>

          {/* ====== Vastram Mirror Try-On Section ====== */}
          {user && (mirrorResult || mirrorGenerating) && (
            <div className="border border-gold/30 rounded-sm overflow-hidden" data-testid="mirror-result-section">
              <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-forest to-forest/90">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-gold" />
                  <span className="text-sm text-ivory font-medium tracking-wide">Your Vastram Mirror Try-On</span>
                </div>
                {mirrorResult && (
                  <button onClick={handleDeleteResult} className="text-ivory/70 hover:text-red-300 transition-colors" data-testid="delete-tryon-result">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="p-5">
                {mirrorGenerating ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Sparkles className="w-12 h-12 text-gold animate-spin" />
                    <p className="text-sm text-forest font-medium">Generating your virtual try-on...</p>
                    <p className="text-xs text-muted-foreground">This may take up to a minute</p>
                  </div>
                ) : mirrorResult?.result_image_url ? (
                  <div className="space-y-3">
                    <img src={mirrorResult.result_image_url} alt="Virtual try-on" className="w-full max-w-sm mx-auto rounded-sm shadow-lg" data-testid="tryon-result-image" />
                    <p className="text-xs text-center text-muted-foreground">Generated {new Date(mirrorResult.created_at).toLocaleDateString()}</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Mirror photo management (subtle) */}
          {user && mirrorPhoto && (
            <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-warm rounded-sm">
              <div className="flex items-center space-x-3">
                <img src={mirrorPhoto} alt="Your mirror photo" className="w-8 h-8 rounded-full object-cover border border-gold/30" />
                <span className="text-xs text-muted-foreground">Your saved mirror photo</span>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => setShowPhotoModal(true)} className="text-xs text-forest hover:text-gold transition-colors">Change</button>
                <button onClick={handleDeletePhoto} className="text-xs text-red-500 hover:text-red-700 transition-colors" data-testid="delete-mirror-photo">Remove</button>
              </div>
            </div>
          )}

          {/* Delivery & Policy */}
          <div className="border-t border-gold/20 pt-6 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-forest/5 flex items-center justify-center flex-shrink-0"><Truck className="w-4 h-4 text-forest" /></div>
              <div><p className="text-sm text-forest font-medium">Estimated Delivery: 3–5 Working Days</p><p className="text-xs text-muted-foreground">Free shipping on orders above {'\u20B9'}2,000</p></div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-forest/5 flex items-center justify-center flex-shrink-0"><RefreshCw className="w-4 h-4 text-forest" /></div>
              <div><p className="text-sm text-forest font-medium">7-Day Easy Returns</p><p className="text-xs text-muted-foreground">Hassle-free returns & exchange</p></div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-forest/5 flex items-center justify-center flex-shrink-0"><CreditCard className="w-4 h-4 text-forest" /></div>
              <div><p className="text-sm text-forest font-medium">Cash on Delivery</p><p className="text-xs text-muted-foreground">Pay when you receive</p></div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-forest/5 flex items-center justify-center flex-shrink-0"><Shield className="w-4 h-4 text-forest" /></div>
              <div><p className="text-sm text-forest font-medium">Authentic Handcraft</p><p className="text-xs text-muted-foreground">100% genuine artisanal product</p></div>
            </div>
          </div>

          {/* Reviews */}
          <div className="border-t border-gold/20 pt-8" data-testid="reviews-section">
            <h2 className="font-heading text-2xl text-forest mb-6">Customer Reviews ({reviews.length})</h2>
            {reviews.length === 0 ? (
              <p className="text-muted-foreground text-sm">No reviews yet. Be the first to review!</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(rev => (
                  <div key={rev.review_id} className="bg-neutral-warm p-5 rounded-sm" data-testid="review-item">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-full bg-forest/10 flex items-center justify-center"><User className="w-4 h-4 text-forest" /></div>
                        <div>
                          <p className="text-forest text-sm font-medium">{rev.user_name || 'Customer'}</p>
                          <p className="text-xs text-muted-foreground">{new Date(rev.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mb-2">
                      <div className="flex items-center space-x-1"><span className="text-xs text-muted-foreground">Product:</span><StarRating rating={rev.product_rating} /></div>
                      <div className="flex items-center space-x-1"><span className="text-xs text-muted-foreground">Delivery:</span><StarRating rating={rev.delivery_rating} /></div>
                    </div>
                    {rev.product_review && <p className="text-sm text-forest mb-1">{rev.product_review}</p>}
                    {rev.delivery_review && <p className="text-sm text-muted-foreground italic">Delivery: {rev.delivery_review}</p>}
                    {rev.media_urls?.length > 0 && (
                      <div className="flex space-x-2 mt-3">
                        {rev.media_urls.map((url, i) => (
                          <button key={i} onClick={() => setEnlargedReviewImage(url)} className="block">
                            {url.includes('.mp4') || url.includes('.mov') ? <video src={url} className="w-20 h-20 object-cover rounded-sm" /> : <img src={url} alt="Review" className="w-20 h-20 object-cover rounded-sm hover:opacity-80 transition-opacity" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && (
        <MirrorPhotoModal
          onClose={() => setShowPhotoModal(false)}
          onPhotoReady={handlePhotoReady}
          productCategory={product.category}
          hasExistingPhoto={!!mirrorPhoto}
        />
      )}

      {/* Enlarged Review Image Modal */}
      {enlargedReviewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEnlargedReviewImage(null)} data-testid="review-image-modal">
          <button onClick={() => setEnlargedReviewImage(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors z-10"
            data-testid="review-image-close">
            <X className="w-5 h-5 text-forest" />
          </button>
          <div className="max-w-3xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
            {enlargedReviewImage.includes('.mp4') || enlargedReviewImage.includes('.mov') ? (
              <video controls playsInline className="max-w-full max-h-[85vh] rounded-sm" src={enlargedReviewImage} />
            ) : (
              <img src={enlargedReviewImage} alt="Review" className="max-w-full max-h-[85vh] object-contain rounded-sm" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
